import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Snackbar,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DefaultFooter from './components/DefaultFooter';
import bannerImg from '../images/background_img_1.png';
import { UserRole } from '../types/role';
import {
  INDEPENDENT_BARBER_PLANS,
  SHOP_OWNER_PLANS,
} from '../defaults/SubscriptionPlans';
import { SubscriptionPlan } from '../types/subscriptionPlan';
import {
  getBarberSubscriptionByBarberId,
  getShopSubscriptionByShopId,
  retrieveStripeSubscription,
} from '../middleware/registration_service/getRequests';
import {
  updateShopSubscription,
  updateBarberSubscription,
} from '../middleware/registration_service/updateRequest';
import { StripeSubscription } from '../types/stripePayments';
import {
  createStripeSubscription,
  updateStripeSubscription,
} from '../middleware/registration_service/createRequest';
import { useAuth0 } from '@auth0/auth0-react';
import { loadStripe } from '@stripe/stripe-js';
import { stripePublishableKey } from '../config';

const stripePromise = loadStripe(stripePublishableKey);

export default function Subscriptions() {
  const { getAccessTokenSilently } = useAuth0();
  const role = localStorage.getItem('role');

  const isBarber = role === UserRole.BARBER;
  const isShopOwner = role === UserRole.SHOP_OWNER;
  // Any role that is not SHOP_OWNER or BARBER (e.g. SHOP_BARBER) cannot manage subscriptions
  const isUnsupportedRole = !isBarber && !isShopOwner;

  const plans = isBarber
    ? INDEPENDENT_BARBER_PLANS
    : isShopOwner
    ? SHOP_OWNER_PLANS
    : [...INDEPENDENT_BARBER_PLANS, ...SHOP_OWNER_PLANS];

  // currentPlanId holds the plan id fetched from the API (matched via name→id)
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<number | null>(null);
  const [isCurrentPlanActive, setIsCurrentPlanActive] = useState<boolean>(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState<
    string | undefined
  >(undefined);
  const [subscriptionItemId, setSubscriptionItemId] = useState<string | null>(
    null
  );
  const [stripeSubscriptionStatus, setStripeSubscriptionStatus] = useState<
    string | null
  >(null);
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  // Tracks whether we're still fetching the current subscription. Used to
  // avoid briefly rendering ALL shop owner plans (including lower-value
  // ones) before currentPlanId is known and the upgrade-only filter can
  // be applied.
  const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = await getAccessTokenSilently();
        if (isShopOwner) {
          const shopIdItem = localStorage.getItem('reg_shop_id');
          if (!shopIdItem) return;
          const res = await getShopSubscriptionByShopId(
            BigInt(shopIdItem),
            token
          );
          if (res.data?.id == null) return;
          setSubscriptionId(res.data.id);
          setCustomerId(res.data.customerId ?? undefined);
          setIsCurrentPlanActive(
            res.data.status === 'active' || res.data.status === 'trialing'
          );
          setStripeSubscriptionId(res.data.stripeSubscriptionId ?? undefined);
          if (res.data.stripeSubscriptionId) {
            try {
              const response = await retrieveStripeSubscription(
                res.data.stripeSubscriptionId,
                token
              );
              const stripeSub = response.data as StripeSubscription;
              setSubscriptionItemId(stripeSub?.items?.data?.[0]?.id ?? null);
              setStripeSubscriptionStatus(stripeSub?.status ?? null);
              // Live Stripe status overrides the possibly-stale local status
              if (stripeSub?.status) {
                setIsCurrentPlanActive(
                  stripeSub.status === 'active' ||
                    stripeSub.status === 'trialing'
                );
              }
            } catch (stripeErr) {
              console.warn(
                'Could not retrieve Stripe subscription (shop):',
                stripeErr
              );
            }
          }
          const matched = SHOP_OWNER_PLANS.find(
            (p) => p.name === res.data.name
          );
          if (matched) {
            setCurrentPlanId(matched.id);
            setSelectedPlanId(matched.id);
          }
        } else if (isBarber) {
          const partnerItem = localStorage.getItem('reg_partner_id');
          if (!partnerItem) return;
          const res = await getBarberSubscriptionByBarberId(
            BigInt(partnerItem),
            token
          );
          if (res.data?.id == null) return;
          setSubscriptionId(res.data.id);
          setCustomerId(res.data.customerId ?? undefined);
          setIsCurrentPlanActive(
            res.data.status === 'active' || res.data.status === 'trialing'
          );
          setStripeSubscriptionId(res.data.stripeSubscriptionId ?? undefined);
          if (res.data.stripeSubscriptionId) {
            try {
              const response = await retrieveStripeSubscription(
                res.data.stripeSubscriptionId,
                token
              );
              const stripeSub = response.data as StripeSubscription;
              setSubscriptionItemId(stripeSub?.items?.data?.[0]?.id ?? null);
              setStripeSubscriptionStatus(stripeSub?.status ?? null);
              // Live Stripe status overrides the possibly-stale local status
              if (stripeSub?.status) {
                setIsCurrentPlanActive(
                  stripeSub.status === 'active' ||
                    stripeSub.status === 'trialing'
                );
              }
            } catch (stripeErr) {
              console.warn(
                'Could not retrieve Stripe subscription (barber):',
                stripeErr
              );
            }
          }
          const matched = INDEPENDENT_BARBER_PLANS.find(
            (p) => p.name === res.data.name
          );
          if (matched) {
            setCurrentPlanId(matched.id);
            setSelectedPlanId(matched.id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
      } finally {
        // Whatever happened above (success, early return, or error), the
        // fetch attempt is done — safe to reveal the (now correctly
        // filtered) plan list.
        setSubscriptionLoading(false);
      }
    };

    fetchSubscription();
  }, [isBarber, isShopOwner, getAccessTokenSilently]);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;
  const isCurrentPlan = selectedPlanId === currentPlanId;

  // Shop owners must only be able to upgrade — never downgrade to a
  // lower-value plan. Once we know their current plan, hide any plan whose
  // price is lower than it from the list entirely (acceptance criteria:
  // "Subscriptions with a lower value should not be displayed").
  const currentShopOwnerPlan = isShopOwner
    ? SHOP_OWNER_PLANS.find((p) => p.id === currentPlanId) || null
    : null;
  const visibleShopOwnerPlans =
    isShopOwner && subscriptionLoading
      ? // Don't reveal any plans (including lower-value ones) until we know
        // the shop owner's current plan and can apply the upgrade-only filter.
        []
      : currentShopOwnerPlan
      ? SHOP_OWNER_PLANS.filter((p) => p.price >= currentShopOwnerPlan.price)
      : SHOP_OWNER_PLANS;

  const handleSelect = (plan: SubscriptionPlan) => {
    if (
      isShopOwner &&
      currentShopOwnerPlan &&
      plan.price < currentShopOwnerPlan.price
    ) {
      // Defense in depth — this plan shouldn't be rendered/selectable at
      // all, but guard here too in case it's ever reachable.
      setError('You can only upgrade to a higher-value subscription plan.');
      return;
    }
    setSelectedPlanId(plan.id);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!selectedPlan) {
      setError('Please select a subscription plan to continue.');
      return;
    }

    if (isCurrentPlan && isCurrentPlanActive) {
      setError('You are already on this plan.');
      return;
    }
    // Shop owners must only be able to upgrade — reject any attempt to
    // switch to a lower-value plan even if it were reached programmatically.
    if (
      isShopOwner &&
      currentShopOwnerPlan &&
      selectedPlan.price < currentShopOwnerPlan.price
    ) {
      setError('You can only upgrade to a higher-value subscription plan.');
      return;
    }
    // Block unsupported roles before any async work starts
    if (isUnsupportedRole) {
      setError(
        'Subscription management is not available for your role. Please contact your shop owner.'
      );
      return;
    }

    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    const expiryDateStr = expiryDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD

    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      // Update Stripe subscription first if we have the IDs
      let stripeSuccess = true;
      let stripeErrorMessage: string | null = null;
      let updatedStripeSubscriptionId: string | undefined;
      if (
        stripeSubscriptionId &&
        selectedPlan.productId &&
        selectedPlan.priceId
      ) {
        try {
          if (
            stripeSubscriptionStatus === 'canceled' ||
            stripeSubscriptionStatus === 'incomplete_expired' ||
            stripeSubscriptionStatus === 'incomplete'
          ) {
            // Subscription was canceled — create a new one instead of updating.
            // subscriptionItemId is NOT needed here (it belongs to the old canceled sub).
            if (!customerId) {
              stripeErrorMessage =
                'Customer ID not found. Cannot create Stripe subscription.';
              stripeSuccess = false;
            } else {
              const response = await createStripeSubscription({
                customerId: customerId,
                item: { priceId: selectedPlan.priceId },
                trialPeriodDays: 0,
              });

              const stripeSubscriptionResponse: StripeSubscription =
                response.data;

              updatedStripeSubscriptionId = stripeSubscriptionResponse?.id;

              // payment_behavior=default_incomplete leaves the subscription
              // 'incomplete' until the invoice's PaymentIntent is confirmed
              // client-side. The card is already the customer's default
              // payment method, so confirmCardPayment finalizes payment
              // without re-collecting card details.
              if (stripeSubscriptionResponse?.status === 'incomplete') {
                const clientSecret =
                  stripeSubscriptionResponse.latestInvoice?.clientSecret;
                if (!clientSecret) {
                  stripeErrorMessage =
                    'Unable to confirm payment for the new subscription. Please try again.';
                  stripeSuccess = false;
                } else {
                  const stripe = await stripePromise;
                  const { error: confirmError } =
                    (await stripe?.confirmCardPayment(clientSecret)) ?? {
                      error: { message: 'Stripe failed to initialise.' },
                    };
                  if (confirmError) {
                    stripeErrorMessage =
                      confirmError.message ??
                      'Payment confirmation failed. Please check your card details.';
                    stripeSuccess = false;
                  }
                }
              }
            }
          } else {
            // Add new price item with proration.
            const response = await updateStripeSubscription(
              stripeSubscriptionId,
              {
                item: {
                  id: subscriptionItemId ?? undefined,
                  priceId: selectedPlan.priceId,
                },
              },
              token
            );

            const updatedSubscription: StripeSubscription = response.data;

            updatedStripeSubscriptionId = updatedSubscription?.id;
          }
        } catch (stripeErr) {
          console.warn('Could not update Stripe subscription:', stripeErr);
          stripeSuccess = false;
        }
      }

      if (!stripeSuccess) {
        setError(
          stripeErrorMessage ??
            'Failed to update Stripe subscription. Please try again.'
        );
        return;
      }

      if (!updatedStripeSubscriptionId) {
        setError(
          'Stripe subscription ID is missing. Cannot update subscription.'
        );
        return;
      }

      if (isShopOwner) {
        const shopIdItem = localStorage.getItem('reg_shop_id');
        if (!shopIdItem) {
          setError('Shop ID not found.');
          return;
        }
        if (subscriptionId === null) {
          setError('Subscription ID not found.');
          return;
        }
        // TypeScript narrows: shopIdItem → string, subscriptionId → number
        await updateShopSubscription(
          subscriptionId,
          {
            name: selectedPlan.name,
            price: selectedPlan.price,
            productId: selectedPlan.productId,
            barberSeatCount: selectedPlan.barberSeatCount ?? 2,
            marketingCampaignCount: selectedPlan.marketingCampaignCount ?? 1,
            emailNotificationCount: selectedPlan.emailNotificationCount ?? 10,
            smsNotificationCount: selectedPlan.smsNotificationCount ?? 10,
            shopId: shopIdItem,
            customerId: customerId,
            stripeSubscriptionId: updatedStripeSubscriptionId,
            expiryDate: expiryDateStr,
            status: 'active',
          },
          token
        );
      } else if (isBarber) {
        const partnerItem = localStorage.getItem('reg_partner_id');
        if (!partnerItem) {
          setError('Barber ID not found.');
          return;
        }
        if (subscriptionId === null) {
          setError('Subscription ID not found.');
          return;
        }
        // TypeScript narrows: partnerItem → string, subscriptionId → number
        await updateBarberSubscription(
          subscriptionId,
          {
            name: selectedPlan.name,
            price: selectedPlan.price,
            productId: selectedPlan.productId,
            marketingCampaignCount: selectedPlan.marketingCampaignCount ?? 5,
            emailNotificationCount: selectedPlan.emailNotificationCount ?? 100,
            smsNotificationCount: selectedPlan.smsNotificationCount ?? 100,
            barberId: partnerItem,
            customerId: customerId,
            stripeSubscriptionId: updatedStripeSubscriptionId,
            expiryDate: expiryDateStr,
            status: 'active',
          },
          token
        );
      } else {
        // Safety net: the isUnsupportedRole guard above should already have
        // returned. setError + return (not throw) keeps this out of the
        // catch block so local state is never updated without an API call.
        setError('Invalid user role. Cannot update subscription.');
        return;
      }

      // Only reached when isShopOwner or isBarber branch completed successfully
      setCurrentPlanId(selectedPlan.id);
      setIsCurrentPlanActive(true);
      if (isCurrentPlan && !isCurrentPlanActive) {
        // Re-activation: reload so all subscription-aware components reflect the new active state
        window.location.reload();
      } else {
        setSuccessOpen(true);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to update subscription. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const headingText = isBarber
    ? 'Your Barber Subscription'
    : isShopOwner
    ? 'Your Shop Subscription'
    : 'Subscription Plans';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Hero Banner */}
      <Box
        sx={{
          mt: '85px',
          backgroundImage: `linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url(${bannerImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderBottom: '4px solid #dd8709',
          py: 8,
          px: 2,
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          gutterBottom
          sx={{ fontFamily: 'GFS Didot' }}
        >
          {headingText}
        </Typography>
        <Typography
          variant="h6"
          sx={{ maxWidth: 640, mx: 'auto', opacity: 0.85 }}
        >
          Elevate your barbing business with Barbers Limited. Upgrade or change
          your plan at any time.
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            mt: 3,
            px: 3,
            py: 1,
            bgcolor: 'rgba(221,135,9,0.2)',
            borderRadius: 8,
            border: '1px solid rgba(221,135,9,0.5)',
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 20 }} />
          <Typography variant="body2">
            No long-term commitment. Cancel or switch plans anytime.
          </Typography>
        </Box>
      </Box>

      {/* Plans */}
      <Container maxWidth="lg" sx={{ py: 6, flexGrow: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Inform shop barbers (and any other unsupported role) upfront */}
        {isUnsupportedRole && role && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Subscription management is handled by your shop owner. You can
            browse plans here but cannot make changes.
          </Alert>
        )}

        {/* Independent Barber section */}
        {(isBarber || (!isBarber && !isShopOwner)) && (
          <Box sx={{ mb: isShopOwner ? 0 : 5 }}>
            {!isBarber && !isShopOwner && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
              >
                <ContentCutIcon sx={{ color: '#2b2b33' }} />
                <Typography variant="h5" fontWeight="bold">
                  Independent Barber
                </Typography>
              </Box>
            )}
            <Grid
              container
              spacing={3}
              justifyContent={isBarber ? 'center' : 'flex-start'}
            >
              {INDEPENDENT_BARBER_PLANS.map((plan) => (
                <Grid item xs={12} sm={6} md={isBarber ? 5 : 4} key={plan.id}>
                  <PlanCard
                    plan={plan}
                    selected={selectedPlanId === plan.id}
                    isCurrent={plan.id === currentPlanId}
                    onSelect={() => handleSelect(plan)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {!isBarber && !isShopOwner && <Divider sx={{ my: 4 }} />}

        {/* Shop Owner section */}
        {(isShopOwner || (!isBarber && !isShopOwner)) && (
          <Box>
            {!isBarber && !isShopOwner && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
              >
                <StorefrontIcon sx={{ color: '#2b2b33' }} />
                <Typography variant="h5" fontWeight="bold">
                  Shop Owner with Barbers
                </Typography>
              </Box>
            )}
            {isShopOwner && subscriptionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Grid
                container
                spacing={3}
                justifyContent={isShopOwner ? 'center' : 'flex-start'}
              >
                {(isShopOwner ? visibleShopOwnerPlans : SHOP_OWNER_PLANS).map(
                  (plan) => (
                    <Grid item xs={12} sm={6} md={4} key={plan.id}>
                      <PlanCard
                        plan={plan}
                        selected={selectedPlanId === plan.id}
                        isCurrent={plan.id === currentPlanId}
                        onSelect={() => handleSelect(plan)}
                      />
                    </Grid>
                  )
                )}
              </Grid>
            )}
            {isShopOwner &&
              currentShopOwnerPlan &&
              visibleShopOwnerPlans.length < SHOP_OWNER_PLANS.length && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, textAlign: 'center' }}
                >
                  Lower-value plans are hidden — you can only upgrade from your
                  current plan.
                </Typography>
              )}
          </Box>
        )}

        {/* Selected plan summary */}
        {selectedPlan && (
          <Box
            sx={{
              mt: 5,
              p: 3,
              bgcolor: '#f0f4ff',
              borderRadius: 2,
              border: '2px solid #1976d2',
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {isCurrentPlan && isCurrentPlanActive
                ? 'Current Plan'
                : isCurrentPlan && !isCurrentPlanActive
                ? 'Inactive Plan'
                : 'Selected Plan'}
              : {selectedPlan.name} — {selectedPlan.subtitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedPlan.trialDays > 0
                ? `Free for ${
                    selectedPlan.trialDays
                  } days, then £${selectedPlan.price.toFixed(2)}/month`
                : `£${selectedPlan.price.toFixed(2)}/month`}
              {selectedPlan.barberSeatCount
                ? ` · ${selectedPlan.barberSeatCount} barber seats`
                : ''}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            disabled={
              !selectedPlan ||
              (isCurrentPlan && isCurrentPlanActive) ||
              loading ||
              isUnsupportedRole
            }
            onClick={handleConfirm}
            sx={{
              px: 6,
              py: 1.5,
              bgcolor: '#dd8709',
              color: '#fff',
              fontWeight: 'bold',
              fontFamily: 'GFS Didot',
              borderRadius: 2,
              '&:hover': { bgcolor: '#b86e08' },
              '&:disabled': { bgcolor: '#e0b96a', color: '#fff' },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isCurrentPlan && isCurrentPlanActive ? (
              'Current Plan'
            ) : isCurrentPlan && !isCurrentPlanActive ? (
              'Re-activate Plan'
            ) : isUnsupportedRole ? (
              'Not Available for Your Role'
            ) : (
              'Switch to this Plan'
            )}
          </Button>
        </Box>
      </Container>

      <DefaultFooter />

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Subscription plan updated successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
}

interface PlanCardProps {
  plan: SubscriptionPlan;
  selected: boolean;
  isCurrent: boolean;
  onSelect: () => void;
}

const PlanCard = ({ plan, selected, isCurrent, onSelect }: PlanCardProps) => (
  <Card
    onClick={onSelect}
    tabIndex={0}
    role={'button'}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === '  ') {
        event.preventDefault();
        onSelect();
      }
    }}
    elevation={selected ? 8 : 3}
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 3,
      cursor: 'pointer',
      border: selected
        ? '2px solid #1976d2'
        : isCurrent
        ? '2px solid #4caf50'
        : '2px solid transparent',
      transition: 'transform 0.2s, box-shadow 0.2s, border 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 6,
      },
      position: 'relative',
    }}
  >
    {/* Current plan badge */}
    {isCurrent && (
      <Box sx={{ position: 'absolute', top: 14, left: 14 }}>
        <Chip
          label="Current Plan"
          size="small"
          sx={{
            bgcolor: '#4caf50',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        />
      </Box>
    )}

    {plan.tag && (
      <Box sx={{ position: 'absolute', top: 14, right: 14 }}>
        <Chip
          label={plan.tag}
          size="small"
          sx={{
            bgcolor: plan.tagColor || '#1976d2',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        />
      </Box>
    )}

    <CardContent sx={{ flexGrow: 1, p: 3, pt: isCurrent ? 5 : 3 }}>
      {/* Icon */}
      <Box sx={{ mb: 2, mt: 2 }}>
        {plan.type === 'BARBER' ? (
          <ContentCutIcon sx={{ fontSize: 36, color: '#2b2b33' }} />
        ) : (
          <StorefrontIcon sx={{ fontSize: 36, color: '#2b2b33' }} />
        )}
      </Box>

      {/* Name + subtitle */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {plan.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {plan.subtitle}
      </Typography>

      {/* Price */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
        {plan.price === 0 ? (
          <Typography variant="h4" fontWeight="bold" color="success.main">
            Free
          </Typography>
        ) : (
          <>
            <Typography variant="h4" fontWeight="bold">
              £{plan.price.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              /month
            </Typography>
          </>
        )}
      </Box>

      {/* Seat count badge */}
      {plan.barberSeatCount && (
        <Chip
          label={`${plan.barberSeatCount} barber seats`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mb: 2 }}
        />
      )}

      <Divider sx={{ my: 2 }} />

      {/* Features */}
      {plan.features.map((feat) => (
        <Box
          key={feat}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
        >
          <CheckCircleRoundedIcon
            sx={{ fontSize: 18, color: selected ? '#1976d2' : '#4caf50' }}
          />
          <Typography variant="body2">{feat}</Typography>
        </Box>
      ))}
    </CardContent>

    {/* Select button */}
    <Box sx={{ p: 2, pt: 0 }}>
      <Button
        fullWidth
        variant={selected ? 'contained' : 'outlined'}
        sx={
          selected
            ? {
                bgcolor: '#dd8709',
                color: '#fff',
                fontWeight: 'bold',
                fontFamily: 'GFS Didot',
                '&:hover': { bgcolor: '#b86e08' },
              }
            : {
                fontWeight: 'bold',
                fontFamily: 'GFS Didot',
                borderColor: '#dd8709',
                color: '#dd8709',
              }
        }
      >
        {isCurrent && selected
          ? '✓ Current Plan'
          : selected
          ? '✓ Selected'
          : 'Select Plan'}
      </Button>
    </Box>
  </Card>
);
